import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Avatar, Divider, Grid, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import Slider from 'react-slick';
import './new-slider.css';
import RestoreDefImg from './Photo.png';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDashboardSupervisorEditAction,
  getTherapistDailyUpdatesAction,
} from 'src/redux/actions/therapist-dashboard-action';
import moment from 'moment';
import FacilityImage from './FacilityImage.png';
import { getInitials } from 'src/utils/get-initials';

const DISCIPLINE_ABBREVIATIONS = new Set(['OT', 'PT', 'SLP', 'DSN', 'PN', 'MDS', 'POC']);

const formatSupervisorEditTitle = (rawTitle: string): string => {
  if (!rawTitle) return 'PN Edit Note';
  // Only apply the snake_case → Title Case transform when the title looks like
  // a BE enum (contains an underscore OR is all uppercase). Pre-formatted titles
  // like "Progress Note" are passed through as-is so existing casing is preserved.
  const isEnumLike = rawTitle.includes('_') || rawTitle === rawTitle.toUpperCase();
  const formatted = isEnumLike
    ? rawTitle
        .split('_')
        .map((word) => {
          const upper = word.toUpperCase();
          if (DISCIPLINE_ABBREVIATIONS.has(upper)) return upper;
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ')
    : rawTitle;
  return `${formatted} Edit Note`;
};

export default function NewSlickSlider() {
  const dispatch = useDispatch();
  const [updates, setUpdates] = useState<any>([]);
  const [greeting, setGreeting] = useState('');
  const providerData = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );
  const therapistDailyUpdates = useSelector(
    (state: any) => state.therapistDailyUpdatesReducer?.response?.data
  );
  const supervisorEditNotes = useSelector(
    (state: any) => state.dashboardSupervisorEditReducer?.response?.data
  );

  useEffect(() => {
    const emptyState = {
      uuid: '0',
      providerName: `${providerData?.lastName ? providerData?.lastName : ''} ${
        providerData?.firstName ? providerData?.firstName : ''
      }`,
      image: RestoreDefImg,
      text: 'Manage your goals, tasks and activities for today, we are here to help .',
    };

    const mappedSupervisorEditNotes = Array.isArray(supervisorEditNotes)
      ? supervisorEditNotes?.map((note: any) => ({
          uuid: note?.uuid,
          type: 'SupervisorEditNote',
          title: note?.title || '',
          providerName: note?.supervisorName || '',
          patientName: note?.patientName || '',
          image: note?.patientImage || '',
          text: note?.taskNote || '',
        }))
      : [];

    const allUpdates = [...(therapistDailyUpdates || []), ...mappedSupervisorEditNotes];

    if (allUpdates?.length) {
      setUpdates([emptyState, ...allUpdates]);
    } else {
      setUpdates([emptyState]);
    }
  }, [therapistDailyUpdates, supervisorEditNotes, providerData]);

  useEffect(() => {
    if (providerData?.providerId) {
      dispatch(
        getTherapistDailyUpdatesAction({
          currentDate: moment(new Date(), 'DD-MM-YYYY').format('YYYY-MM-DD'),
          providerId: providerData.providerId,
        })
      );
      dispatch(getDashboardSupervisorEditAction());
    }
  }, [providerData?.providerId, dispatch]);

  useEffect(() => {
    const updateGreeting = () => {
      const currentHour = new Date().getHours();

      if (currentHour >= 5 && currentHour < 12) {
        setGreeting('Good Morning');
      } else if (currentHour >= 12 && currentHour < 17) {
        setGreeting('Good Afternoon');
      } else {
        setGreeting('Good Evening');
      }
    };

    updateGreeting();

    const intervalId = setInterval(updateGreeting, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const sliderSettings = {
    arrows: true,
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    cssEase: 'linear',
    nextArrow: (
      <ChevronRight
        sx={{
          width: '30px',
          height: '30px',
          color: '#6C737F',
          ':hover': {
            color: 'ActiveCaption',
            backgroundColor: 'transparent',
            borderRadius: '120px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          },
        }}
      />
    ),
    prevArrow: (
      <ChevronLeft
        sx={{
          width: '30px',
          height: '30px',
          color: '#6C737F',
          ':hover': {
            color: 'ActiveCaption',
            backgroundColor: 'transparent',
            borderRadius: '120px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          },
        }}
      />
    ),
  };

  return (
    <Slider {...sliderSettings}>
      {updates.map((item: any) => (
        <div key={item.uuid}>
          {item.uuid === '0' ? (
            <Grid
              display={'flex'}
              flexDirection={'row'}
              height="200px"
              sx={{ position: 'relative', m: '10px 50px' }}
              gap={'10px'}
            >
              <Box
                alignContent={'center'}
                p={'15px'}
              >
                <img
                  src={item?.image ? item?.image : RestoreDefImg}
                  alt={`Image for ${item.uuid}`}
                  style={{ width: '100px', height: '100px' }}
                />
              </Box>
              <Box
                width={'100%'}
                alignContent={'center'}
                p={'15px'}
              >
                <Typography
                  sx={{
                    color: '#7375F2',
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '22px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '120%',
                  }}
                >
                  {greeting}
                </Typography>
                <Typography
                  sx={{
                    color: '#6C737F',
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '150%',
                  }}
                >
                  {item.providerName}
                </Typography>
              </Box>
            </Grid>
          ) : (
            <Grid
              display={'flex'}
              flexDirection={'row'}
              height="200px"
              sx={{ position: 'relative', m: '10px 50px' }}
              gap={'10px'}
            >
              <Box
                alignContent={'center'}
                p={'15px'}
              >
                <Avatar
                  src={
                    item?.type === 'Announcement'
                      ? FacilityImage
                      : item?.image
                      ? `data:image/png;base64,${item?.image}`
                      : ''
                  }
                  sx={{
                    height: 100,
                    width: 100,
                    fontSize: '30px',
                  }}
                >
                  {getInitials(item?.patientName || ' ')}
                </Avatar>
              </Box>
              <Box
                width={'100%'}
                alignContent={'center'}
                p={'10px'}
              >
                <Box>
                  <Typography
                    sx={{
                      color: 'var(--text-primary, #111927)',
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '160%',
                    }}
                  >
                    {item.type === 'Announcement'
                      ? 'Announcement'
                      : item?.type === 'SupervisorEditNote'
                      ? formatSupervisorEditTitle(item?.title)
                      : 'DOR Note'}{' '}
                    <Typography
                      component="span"
                      sx={{
                        color: 'var(--text-secondary, #6C737F)',
                        fontFamily: 'Inter',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '166%',
                      }}
                    >
                      {'by ' + item.providerName}
                    </Typography>
                  </Typography>
                </Box>
                <Divider sx={{ m: '5px', borderWidth: '1.5px' }} />
                <Box>
                  <Typography
                    sx={{
                      mt: '5px',
                      color: '#7375F2',
                      fontFamily: 'Plus Jakarta Sans',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: '21.6px',
                    }}
                  >
                    {item.patientName ? item.patientName : 'Team Announcement'}
                  </Typography>
                  <Tooltip
                    title={item.text.length > 100 ? item.text : ''}
                    placement="top"
                  >
                    <Typography
                      sx={{
                        display: '-webkit-box',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        height: '78px',
                        width: '100%',
                        maxWidth: '100%',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        fontSize: '16px',
                        color: '#6C737F',
                        fontFamily: 'Inter',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '120%',
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          )}
        </div>
      ))}
    </Slider>
  );
}
